import { Accordion, Pill } from '@mantine/core';
import { Minus, Plus } from 'lucide-react';
import { FC, useMemo } from 'react';

import Unavailable from '@/components/Unavailable.tsx';
import TimeslotCalendar from '@/components/booking/TimeslotCalendar.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { rowInteractionKey } from '@/constants/availability-telemetry.ts';

import useTimeslots from '@/hooks/useTimeslots.ts';

import { Insurance } from '@/models/appointment/Appointment.ts';
import { EnrichedAppointmentType, Practitioner } from '@/models/appointment/EnrichedPractitionerData.ts';
import { Timings } from '@/models/doc-cirrus/DCCalendar.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';

interface Props {
    practitioner: Practitioner;
    selectedCalendar?: string;
    isOpen?: boolean;
    insurance: Insurance;
    appointmentType?: EnrichedAppointmentType;
    selectedSlot?: string;
    onSlotSelect: (calendarId: string, slot: string) => void;
    defaultStartDate?: string;
}

const TimeslotCalendarAccordionItem: FC<Props> = ({
    practitioner,
    selectedCalendar,
    isOpen,
    insurance,
    appointmentType,
    selectedSlot,
    onSlotSelect,
    defaultStartDate,
}) => {
    const isReturning = useBookingStore((state) => state.is_returning_patient);

    const calendarId = useMemo(() => {
        if (selectedCalendar && practitioner.calendars.some((cal) => cal.pvs.id === selectedCalendar))
            return selectedCalendar;

        return practitioner.calendars.find((cal) => appointmentType?.pvs.pvs_calendar_ids.includes(cal.pvs.id))?.pvs.id;
    }, [practitioner, appointmentType]);

    const {
        availability,
        is_loading: isLoading,
        error,
        start_date: startDate,
        pageTo,
        has_slots: hasSlots,
        next_available_slot: nextAvailableSlot,
    } = useTimeslots({
        practitioner: practitioner,
        insurance: insurance,
        mode: 'single',
        // A row whose calendar could not be resolved still pages, so it needs a key of its own.
        interactionKey: rowInteractionKey(calendarId ?? practitioner.id),
        isExpandedRow: isOpen,
        appointmentType: appointmentType,
        isReturning: isReturning,
        defaultStartDate: defaultStartDate,
        selectedCalendarId: calendarId,
    });

    // Get timing data for the selected calendar
    const calendarTimings = useMemo((): Timings | undefined => {
        const matchedCalendar = practitioner.calendars.find((cal) => cal.pvs.id === calendarId);

        // Check if the calendar has timings (DCCalendar type)
        if (matchedCalendar?.pvs && 'timings' in matchedCalendar.pvs) {
            return matchedCalendar.pvs.timings;
        }

        // For DataCur calendars, get timings from the practitioner's calendar_timings_map
        if (calendarId && practitioner.calendar_timings_map?.has(calendarId)) {
            return practitioner.calendar_timings_map.get(calendarId);
        }

        return undefined;
    }, [practitioner.calendars, practitioner.calendar_timings_map, calendarId]);

    const onSelect = (slot: string): void => {
        onSlotSelect(calendarId ?? '', slot);
    };

    return (
        <Accordion.Item key={practitioner.id} value={practitioner.id}>
            <Accordion.Control
                className="gap-3 p-0 text-lg"
                chevron={isOpen && availability ? <Minus className="size-6" /> : <Plus className="size-6" />}
                disabled={!availability && error === undefined}
            >
                <div className="flex items-center">
                    <Paragraph className="flex-grow font-semibold">{practitioner.name}</Paragraph>
                    {nextAvailableSlot && (
                        <Pill className="h-6 text-sm">{dayjs(nextAvailableSlot).format('D MMM')}</Pill>
                    )}
                </div>
            </Accordion.Control>
            <Accordion.Panel>
                {error ? (
                    <Unavailable text="Zeitfenster konnten nicht geladen werden" />
                ) : (
                    <TimeslotCalendar
                        calendarId={calendarId}
                        availability={availability}
                        isLoading={isLoading}
                        startDate={startDate}
                        pageTo={pageTo}
                        hasSlots={hasSlots}
                        nextAvailableSlot={nextAvailableSlot}
                        onSlotSelect={onSelect}
                        selectedSlot={selectedSlot}
                        timings={calendarTimings}
                    />
                )}
            </Accordion.Panel>
        </Accordion.Item>
    );
};

export default TimeslotCalendarAccordionItem;
